import { api, API_BASE_URL } from './client';
import {
  Employee,
  EmployeeUpdateData,
  EmployeeEvaluationResponse,
  EvaluationCreateData,
  EmployeeStatus,
  EmployeeStatusUpdateData,
  EmployeesResponse,
  ExtractedEmployeeResponse,
  ManualEmployeeCreateData,
  ManualEmployeeResponse,
  PdfUploadResponse,
  PersonnelChange,
  PersonnelChangeCreateData,
  PersonnelChangesResponse,
} from './types';

export const employeesApi = {
  getAllEmployees: async (page: number = 1, limit: number = 100, status: string = 'active'): Promise<{ employees: Employee[]; total: number; access_info?: { role: string; scope: string; org_units: { name: string; unit_type: string }[]; employee_id: string } }> => {
    const response = await api.get<EmployeesResponse>('/api/employees', {
      params: { page, limit, status }
    });
    return {
      employees: response.data.employees,
      total: response.data.pagination.total,
      access_info: (response.data as any).access_info,
    };
  },

  getEmployee: async (employeeId: string, refresh: boolean = false): Promise<Employee> => {
    const response = await api.get(`/api/employees/${employeeId}`, {
      params: refresh ? { refresh: true } : {}
    });
    return response.data;
  },

  updateEmployee: async (employeeId: string, data: EmployeeUpdateData): Promise<{ message: string; employee: Employee; reanalyzed?: boolean; reanalyze_blocked?: string }> => {
    const response = await api.put(`/api/employees/${employeeId}`, data);
    return response.data;
  },

  deleteEmployee: async (employeeId: string, hardDelete: boolean = false): Promise<{ message: string; employee_id: string; delete_type: string }> => {
    const response = await api.delete(`/api/employees/${encodeURIComponent(employeeId)}`, {
      params: hardDelete ? { hard_delete: true } : {}
    });
    return response.data;
  },

  extractEmployeeData: async (file: File): Promise<ExtractedEmployeeResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/employees/extract', formData);
    return response.data;
  },

  uploadEmployeePdf: async (file: File, team: string, overwrite: boolean = false): Promise<PdfUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('team', team);
    formData.append('overwrite', String(overwrite));
    const response = await api.post('/api/employees/upload', formData);
    return response.data;
  },

  createEmployeeManual: async (data: ManualEmployeeCreateData): Promise<ManualEmployeeResponse> => {
    const response = await api.post('/api/employees', data);
    return response.data;
  },

  // === 직원 평가 히스토리 ===
  getEmployeeEvaluations: async (employeeId: string): Promise<EmployeeEvaluationResponse> => {
    const response = await api.get(`/api/employees/${encodeURIComponent(employeeId)}/evaluations`);
    return response.data;
  },

  addEmployeeEvaluation: async (employeeId: string, data: EvaluationCreateData): Promise<{ message: string; evaluation_id: number; reanalyzed: boolean; employee?: Employee }> => {
    const response = await api.post(`/api/employees/${encodeURIComponent(employeeId)}/evaluations`, data);
    return response.data;
  },

  deleteEmployeeEvaluation: async (employeeId: string, evalId: number): Promise<{ message: string; deleted_id: number }> => {
    const response = await api.delete(`/api/employees/${encodeURIComponent(employeeId)}/evaluations/${evalId}`);
    return response.data;
  },

  // === 인력 변동 ===
  getEmployeePersonnelChanges: async (employeeId: string, limit: number = 50): Promise<{ employee_id: string; changes: PersonnelChange[]; total: number }> => {
    const response = await api.get(`/api/employees/${encodeURIComponent(employeeId)}/personnel-changes`, { params: { limit } });
    return response.data;
  },

  addPersonnelChange: async (employeeId: string, data: PersonnelChangeCreateData): Promise<{ message: string; change_id: number; employee_id: string }> => {
    const response = await api.post(`/api/employees/${encodeURIComponent(employeeId)}/personnel-changes`, data);
    return response.data;
  },

  // === 직원 상태 ===
  getEmployeeStatus: async (employeeId: string): Promise<EmployeeStatus> => {
    const response = await api.get(`/api/employees/${encodeURIComponent(employeeId)}/status`);
    return response.data;
  },

  updateEmployeeStatus: async (employeeId: string, data: EmployeeStatusUpdateData): Promise<{ message: string; employee_id: string; status: string }> => {
    const response = await api.put(`/api/employees/${encodeURIComponent(employeeId)}/status`, data);
    return response.data;
  },

  getAllEmployeeStatuses: async (): Promise<{ statuses: EmployeeStatus[]; total: number }> => {
    const response = await api.get('/api/employees/statuses/all');
    return response.data;
  },

  getEmployeesByStatus: async (status: string): Promise<{ status: string; employees: EmployeeStatus[]; total: number }> => {
    const response = await api.get(`/api/employees/statuses/by-status/${encodeURIComponent(status)}`);
    return response.data;
  },

  // === 직원 사진 ===
  uploadEmployeePhoto: async (employeeId: string, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/api/employees/${encodeURIComponent(employeeId)}/photo`, formData);
    return response.data;
  },

  getEmployeePhotoUrl: (employeeId: string): string => {
    if (!employeeId) return '';
    return `${API_BASE_URL}/api/employees/${encodeURIComponent(employeeId)}/photo`;
  },

  deleteEmployeePhoto: async (employeeId: string): Promise<any> => {
    const response = await api.delete(`/api/employees/${encodeURIComponent(employeeId)}/photo`);
    return response.data;
  },

  // === 직급/직책 ===
  getRanks: async (): Promise<any[]> => {
    const response = await api.get('/api/ranks');
    return response.data?.ranks || [];
  },

  getTitles: async (): Promise<any[]> => {
    const response = await api.get('/api/titles');
    return response.data?.titles || [];
  },

  createTitle: async (name: string, level: number = 0): Promise<any> => {
    const response = await api.post('/api/titles', { name, level });
    return response.data?.title;
  },

  getEmployeeTitles: async (employeeId: string): Promise<any[]> => {
    const response = await api.get(`/api/employees/${encodeURIComponent(employeeId)}/titles`);
    return response.data?.titles || [];
  },

  setEmployeeTitles: async (employeeId: string, titleIds: number[]): Promise<any[]> => {
    const response = await api.put(`/api/employees/${encodeURIComponent(employeeId)}/titles`, { title_ids: titleIds });
    return response.data?.titles || [];
  },

  assignRank: async (employeeId: string, rankId: number, reason?: string, effectiveDate?: string): Promise<any> => {
    const response = await api.post(`/api/employees/${encodeURIComponent(employeeId)}/rank`, {
      rank_id: rankId,
      reason: reason || '변경',
      effective_date: effectiveDate || new Date().toISOString().split('T')[0],
    });
    return response.data;
  },

  assignTitle: async (employeeId: string, titleId: number, reason?: string, effectiveDate?: string): Promise<any> => {
    const response = await api.post(`/api/employees/${encodeURIComponent(employeeId)}/title`, {
      title_id: titleId,
      reason: reason || '변경',
      effective_date: effectiveDate || new Date().toISOString().split('T')[0],
    });
    return response.data;
  },

  getJobCategories: async (): Promise<any[]> => {
    const response = await api.get('/api/job-categories');
    return response.data?.categories || [];
  },

  // === 특이사항 (태그/상벌/코멘트) ===
  getTagTypes: async (): Promise<Record<string, string>> => {
    const response = await api.get('/api/employees/meta/tag-types');
    return response.data?.tag_types || {};
  },

  getDisciplinaryTypes: async (): Promise<Record<string, string>> => {
    const response = await api.get('/api/employees/meta/disciplinary-types');
    return response.data?.disciplinary_types || {};
  },

  getEmployeeNotesSummary: async (employeeId: string): Promise<any> => {
    const response = await api.get(`/api/employees/${employeeId}/notes-summary`);
    return response.data;
  },

  addEmployeeTag: async (employeeId: string, tagType: string, note?: string): Promise<any> => {
    const response = await api.post(`/api/employees/${employeeId}/tags`, { tag_type: tagType, note });
    return response.data;
  },

  removeEmployeeTag: async (employeeId: string, tagId: number): Promise<any> => {
    const response = await api.delete(`/api/employees/${employeeId}/tags/${tagId}`);
    return response.data;
  },

  addDisciplinaryRecord: async (employeeId: string, data: { action_type: string; title: string; description?: string; action_date?: string }): Promise<any> => {
    const response = await api.post(`/api/employees/${employeeId}/disciplinary`, data);
    return response.data;
  },

  deleteDisciplinaryRecord: async (employeeId: string, recordId: number): Promise<any> => {
    const response = await api.delete(`/api/employees/${employeeId}/disciplinary/${recordId}`);
    return response.data;
  },

  addHRComment: async (employeeId: string, comment: string, category?: string): Promise<any> => {
    const response = await api.post(`/api/employees/${employeeId}/hr-comments`, { comment, category });
    return response.data;
  },

  deleteHRComment: async (employeeId: string, commentId: number): Promise<any> => {
    const response = await api.delete(`/api/employees/${employeeId}/hr-comments/${commentId}`);
    return response.data;
  },

  // === 직원 정보 수정 요청 (BACK-165) ===
  getEditableFields: async (): Promise<{ field: string; label: string }[]> => {
    const response = await api.get('/api/edit-requests/fields/editable');
    return response.data;
  },

  createEditRequest: async (data: { field_name: string; current_value?: string; requested_value: string; reason?: string }): Promise<any> => {
    const response = await api.post('/api/edit-requests', data);
    return response.data;
  },

  getMyEditRequests: async (): Promise<any[]> => {
    const response = await api.get('/api/edit-requests/my');
    return response.data;
  },

  getEditRequests: async (params?: { status?: string; employee_id?: string; limit?: number; offset?: number }): Promise<{ requests: any[]; total: number }> => {
    const response = await api.get('/api/edit-requests', { params });
    return response.data;
  },

  getEditRequestDetail: async (id: number): Promise<any> => {
    const response = await api.get(`/api/edit-requests/${id}`);
    return response.data;
  },

  approveEditRequest: async (id: number, comment?: string): Promise<any> => {
    const response = await api.put(`/api/edit-requests/${id}/approve`, { comment });
    return response.data;
  },

  rejectEditRequest: async (id: number, comment?: string): Promise<any> => {
    const response = await api.put(`/api/edit-requests/${id}/reject`, { comment });
    return response.data;
  },

  cancelEditRequest: async (id: number): Promise<any> => {
    const response = await api.put(`/api/edit-requests/${id}/cancel`);
    return response.data;
  },

  getEditRequestPendingCount: async (): Promise<{ count: number }> => {
    const response = await api.get('/api/edit-requests/pending-count');
    return response.data;
  },

  // === 코드 체계 조회 ===
  getCodeList: async (type: 'ranks' | 'titles' | 'job-categories' | 'org-units'): Promise<any[]> => {
    const response = await api.get(`/api/codes/${type}`);
    return response.data || [];
  },

  // === 퇴직 예측 & 리텐션 ===
  getRetentionDashboard: async (): Promise<any> => {
    const response = await api.get('/api/retention/dashboard');
    return response.data;
  },

  getEmployeeRetentionRisk: async (employeeId: string): Promise<any> => {
    const response = await api.get(`/api/retention/employee/${encodeURIComponent(employeeId)}`);
    return response.data;
  },

  // === 발령이력 타임라인 ===
  getEmployeePersonnelTimeline: async (employeeId: string): Promise<any> => {
    const response = await api.get(`/api/personnel-changes/employee/${encodeURIComponent(employeeId)}/timeline`);
    return response.data;
  },
};

// Helper function — pure, no API call
export function formatEmployeeTitles(emp: Employee): string {
  const titles = emp.titles;
  if (titles && titles.length > 0) {
    return titles.map(t => t.name).join(' / ');
  }
  return emp.position || '';
}
